import { RefObject, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import NoResults from '~/components/molecules/noResults/noResults';
import Pager from '~/components/molecules/pager/pager';
import Settings from '~/components/molecules/settings/settings';
import AppliedFilters from '~/components/organisms/filters/appliedFilters/AppliedFilters';
import productStyles from '~/components/organisms/products/productSingleResult/product.module.scss';
import {
    CustomProductColorsConfiguration,
    ProductResultContainer as ProductSingleResultContainer,
} from '~/components/organisms/products/productSingleResult/productResultContainer';
import { Filters_ControlsState, Filters_State } from '~/core/features/filtering/types/filters.type';
import { SelectedSort, SortingMethods } from '~/core/features/sorting/types/sorting.type';
import { useSERPState } from '~/core/state/SERPStateContext';
import { ProductResults_State } from '~/core/state/types/state-results.schema';
import { CancelAllFilters, CancelFilter, SortResults } from '~/core/useSearchState';
import { GoToResultHandler } from '~/hooks/useGoToResult';
import { useSelectedTab } from '~/hooks/useSelectedTab';
import { useShouldRenderSet } from '~/hooks/useShouldRenderSet';
import { useDynamicComponents } from '~/plugins/dynamicComponents/dynamicComponentsContext';
import style from './productResults.module.scss';

type ProductResultsProps = {
    productResults: ProductResults_State;
    loadMore: () => void;
    customColorsConfiguration: CustomProductColorsConfiguration;
    handleGoToResult: GoToResultHandler;
    appliedFiltersState: Filters_State;
    filtersControls: Filters_ControlsState;
    cancelFilter: CancelFilter;
    cancelAllFilters: CancelAllFilters;
    selectedSort: SelectedSort;
    sortResults: SortResults;
    sortingMethods: SortingMethods;
    hasFulltextResults: boolean;
    categoryResultsCount: number;
    inViewRef: RefObject<HTMLDivElement>;
};

const ProductResults = ({
    productResults,
    loadMore,
    customColorsConfiguration,
    appliedFiltersState,
    filtersControls,
    handleGoToResult,
    cancelFilter,
    cancelAllFilters,
    selectedSort,
    sortingMethods,
    sortResults,
    hasFulltextResults,
    categoryResultsCount,
    inViewRef,
}: ProductResultsProps) => {
    const { ProductSingleResult } = useDynamicComponents();
    const state = useSERPState();
    const shouldRenderOnMobile = useShouldRenderSet({ identifier: productResults.querySetIdentifier });
    const { activeTab } = useSelectedTab();

    return (
        <div
            ref={inViewRef}
            className={style.serpResultsCol2}
            data-testid="products-results"
            id={`tabpanel-${activeTab?.title}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab?.title}`}
        >
            {/* Can not render null in root - the ref needs to be preserved with how it is written */}
            {!shouldRenderOnMobile ? null : (
                <>
                    {appliedFiltersState.length > 0 ? (
                        <AppliedFilters
                            filtersState={appliedFiltersState}
                            filtersControls={filtersControls}
                            cancelFilter={cancelFilter}
                            querySetIdentifier={productResults.querySetIdentifier}
                            cancelAllFilters={cancelAllFilters}
                        />
                    ) : null}

                    {productResults.hits.length > 0 ? (
                        <Settings
                            productCurrentCount={productResults.hits.length}
                            productTotalCount={productResults.totalRecords}
                            selectedSort={selectedSort}
                            sortingMethods={sortingMethods}
                            categoryResultsCount={categoryResultsCount}
                            // eslint-disable-next-line @typescript-eslint/no-shadow
                            sortResults={(selectedSort) => {
                                sortResults({
                                    querySetIdentifier: productResults.querySetIdentifier,
                                    selectedSort,
                                });
                            }}
                        />
                    ) : null}

                    {productResults.hits.length === 0 ? (
                        <NoResults />
                    ) : (
                        <div
                            className={classNames(style.serpCards, {
                                [style.productsOnly]: !hasFulltextResults,
                                [style.rows]: state.resultLayout === 'rows',
                            })}
                            data-testid="product-results-cards"
                        >
                            {productResults.hits.map((result, index) => (
                                <ProductSingleResultContainer
                                    key={index}
                                    result={result}
                                    customColorsConfiguration={customColorsConfiguration}
                                    order={index + 1}
                                    handleGoToResult={handleGoToResult}
                                    productStyles={productStyles}
                                >
                                    <ProductSingleResult />
                                </ProductSingleResultContainer>
                            ))}
                        </div>
                    )}
                    {productResults.hits.length < productResults.totalRecords ? <Pager loadMore={loadMore} /> : null}
                </>
            )}
        </div>
    );
};

export default ProductResults;
